/**
 * Admin Users Page
 *
 * The list only. Everything that used to open in a 450px right-hand drawer now lives at
 * /users/:userId — see AdminUserDetailPage.
 *
 * The table is the shared <DataTable>, so the same column definitions render as a table on a
 * desktop and as one card per user below 600px, where the old table's right-hand columns were
 * simply cut off.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  FilterList,
  Refresh,
  Search,
  VolumeOff,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { adminApi } from '@api/endpoints/admin'
import type { AdminUserDto, AdminUsersListParams } from '@api/types/admin'
import { UserFlags } from '@api/types/auth'
import { hasUserFlag } from '@utils/userFlags'
import { PageShell } from '@components/common/PageShell'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { ADMIN_USERS_KEY } from '../components/users/userQueryKeys'
import { errorMessage, formatDate } from '../components/users/userFormatting'
import { useIsAdmin } from '../components/users/useIsAdmin'

type SortField = NonNullable<AdminUsersListParams['sortBy']>

/**
 * The controller binds the active filter from a query parameter called `active`, not `isActive`
 * (AdminUsersController.GetUsers), so the typed field on its own has never filtered anything.
 * Both names go on the wire — ApiService drops the undefined one, and the API ignores the spare.
 */
type UsersQueryParams = AdminUsersListParams & { active?: boolean }

/** Typing in the search box should not fire a request per keystroke. */
const SEARCH_DEBOUNCE_MS = 350

function roleColor(role: string): 'error' | 'warning' | 'default' {
  if (role === 'Admin') return 'error'
  if (role === 'Moderator') return 'warning'
  return 'default'
}

export default function AdminUsersPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // Moderators read the list; the Silent switch is shown but disabled for them (Admin-only on the API).
  const isAdmin = useIsAdmin()

  // Pagination (0-based here, 1-based on the wire)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)

  // Sorting
  const [sortBy, setSortBy] = useState<SortField>('DisplayName')
  const [sortDescending, setSortDescending] = useState(false)

  // Filters
  const [search, setSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('')
  const [activeFilter, setActiveFilter] = useState<boolean | ''>('')
  const [showFilters, setShowFilters] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearch(search)
      // A narrower result set makes the current page number meaningless.
      setPage(0)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])

  const params = useMemo<UsersQueryParams>(
    () => ({
      page: page + 1,
      pageSize,
      sortBy,
      sortDescending,
      search: appliedSearch || undefined,
      role: roleFilter || undefined,
      isActive: activeFilter === '' ? undefined : activeFilter,
      active: activeFilter === '' ? undefined : activeFilter,
    }),
    [page, pageSize, sortBy, sortDescending, appliedSearch, roleFilter, activeFilter]
  )

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: [...ADMIN_USERS_KEY, params],
    queryFn: () => adminApi.getUsers(params),
  })

  // The endpoint pages, filters, sorts and counts server-side and answers with an envelope, so
  // the count is real. It used to return a bare array with no total — the pager said "1–25 of 25"
  // and nobody could reach user 26 — and this page carried a fallback that estimated one. That
  // shape no longer exists.
  const rows: AdminUserDto[] = data?.items ?? []
  const total = data?.totalCount ?? 0

  const {
    mutate: toggleSilentFlag,
    isPending: togglingSilentFlag,
    error: silentFlagError,
  } = useMutation({
    mutationFn: (user: AdminUserDto) => {
      const hasSilentFlag = hasUserFlag(user.userFlags, UserFlags.CanCreateSilentAdventures)
      return hasSilentFlag
        ? adminApi.updateUserFlags(user.id, { removeFlags: UserFlags.CanCreateSilentAdventures })
        : adminApi.updateUserFlags(user.id, { addFlags: UserFlags.CanCreateSilentAdventures })
    },
    // The old page patched its local rows by `u.userId`, a field AdminUserDto does not have, so the
    // switch never actually moved until something else refetched. Invalidating is both shorter and
    // correct.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY }),
    onSettled: () => setSilentTarget(null),
  })
  // A switch flips on a stray thumb while scrolling the phone cards; granting or revoking a
  // permission goes through a confirm instead of happening on contact.
  const [silentTarget, setSilentTarget] = useState<AdminUserDto | null>(null)
  const silentTargetHas = silentTarget
    ? hasUserFlag(silentTarget.userFlags, UserFlags.CanCreateSilentAdventures)
    : false

  const handleSort = (sortKey: string) => {
    const field = sortKey as SortField
    if (sortBy === field) {
      setSortDescending((prev) => !prev)
    } else {
      setSortBy(field)
      setSortDescending(false)
    }
    setPage(0)
  }

  const columns = useMemo<DataTableColumn<AdminUserDto>[]>(
    () => [
      {
        key: 'user',
        header: 'User',
        sortKey: 'DisplayName',
        // The card title on a phone.
        primary: true,
        render: (user) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
            <Avatar src={user.avatar || user.profilePictureUrl} sx={{ width: 40, height: 40 }}>
              {user.displayName?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight="bold">
                {user.displayName}
              </Typography>
              {user.userName && (
                <Typography variant="caption" color="text.secondary" display="block">
                  @{user.userName}
                </Typography>
              )}
              {/* Only when there is something to say. A "0 mocked" badge on every row would read as
                  a clean bill of health, and it is not one: zero is also what an iPhone and a
                  browser report, because neither can tell. */}
              {!!user.mockedLocationReports && (
                <Tooltip
                  title={
                    `${user.mockedLocationReports} location report` +
                    `${user.mockedLocationReports === 1 ? '' : 's'} flagged by the device as mocked` +
                    (user.lastMockedLocationAt
                      ? `, most recently ${new Date(user.lastMockedLocationAt).toLocaleString()}`
                      : '') +
                    '. Evidence, not proof — it does not catch a rooted device, and it is reported by the app itself.'
                  }
                >
                  <Chip
                    icon={<WarningAmberIcon />}
                    label={`Mocked ×${user.mockedLocationReports}`}
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ mt: 0.5, height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
        ),
      },
      {
        key: 'email',
        header: 'Email',
        sortKey: 'Email',
        hideBelow: 'sm',
        render: (user) => (
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {user.email}
          </Typography>
        ),
      },
      {
        key: 'roles',
        header: 'Roles',
        render: (user) => (
          // inline-flex so the chips follow the card's right-aligned value column on a phone
          <Box sx={{ display: 'inline-flex', gap: 0.5, flexWrap: 'wrap' }}>
            {(user.roles || []).map((role) => (
              <Chip key={role} label={role} size="small" color={roleColor(role)} variant="outlined" />
            ))}
          </Box>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (user) => (
          <Chip
            label={user.isActive ? 'Active' : 'Inactive'}
            color={user.isActive ? 'success' : 'default'}
            size="small"
          />
        ),
      },
      {
        key: 'lastLogin',
        header: 'Last Login',
        sortKey: 'LastLoginAt',
        hideBelow: 'md',
        render: (user) => <Typography variant="body2">{formatDate(user.lastLoginAt)}</Typography>,
      },
      {
        key: 'silent',
        header: (
          <Tooltip title="Permission to create silent adventures">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <VolumeOff fontSize="small" />
              Silent
            </Box>
          </Tooltip>
        ),
        align: 'center',
        width: 90,
        render: (user) => (
          // The row (and the card) navigates; the switch must not take the reader with it.
          <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex' }}>
            <Switch
              checked={hasUserFlag(user.userFlags, UserFlags.CanCreateSilentAdventures)}
              onChange={() => setSilentTarget(user)}
              disabled={!isAdmin || togglingSilentFlag}
              color="primary"
              size="small"
            />
          </Box>
        ),
      },
    ],
    [togglingSilentFlag, isAdmin]
  )

  const actions = (
    <>
      <Button
        variant={showFilters ? 'contained' : 'outlined'}
        startIcon={<FilterList />}
        onClick={() => setShowFilters(!showFilters)}
      >
        Filters
      </Button>
      <Button variant="outlined" startIcon={<Refresh />} onClick={() => refetch()} disabled={isFetching}>
        Refresh
      </Button>
    </>
  )

  return (
    <PageShell title="Users" actions={actions}>
      <Paper sx={{ mb: 2, p: { xs: 1.5, sm: 2 } }}>
        <TextField
          fullWidth
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />

        {showFilters && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mt: 2,
              flexWrap: 'wrap',
            }}
          >
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={roleFilter}
                label="Role"
                onChange={(e) => {
                  setRoleFilter(e.target.value)
                  setPage(0)
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Moderator">Moderator</MenuItem>
                <MenuItem value="User">User</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                // MUI wants a primitive here and the filter is a tri-state, so '' stands for "All".
                value={activeFilter === '' ? '' : String(activeFilter)}
                label="Status"
                onChange={(e) => {
                  const value = e.target.value
                  setActiveFilter(value === '' ? '' : value === 'true')
                  setPage(0)
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="text"
              onClick={() => {
                setSearch('')
                setRoleFilter('')
                setActiveFilter('')
                setPage(0)
              }}
            >
              Clear Filters
            </Button>
          </Box>
        )}
      </Paper>

      {silentFlagError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage(silentFlagError, 'Failed to update user flags')}
        </Alert>
      )}

      <DataTable
        rows={rows}
        columns={columns}
        // Two lines per user on a phone, not one labelled line per column. The generic card was
        // honest but screen-high, and twenty-five of them put the pager a long scroll away.
        renderCard={(user) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Avatar src={user.avatar || user.profilePictureUrl} sx={{ width: 40, height: 40, flexShrink: 0 }}>
              {user.displayName?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" fontWeight="bold" noWrap>
                {user.displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {user.email}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={user.isActive ? 'Active' : 'Inactive'}
                  color={user.isActive ? 'success' : 'default'}
                  sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                />
                {user.roles.map((role) => (
                  <Chip
                    key={role}
                    size="small"
                    label={role}
                    variant="outlined"
                    sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                  />
                ))}
                {!!user.mockedLocationReports && (
                  <Chip
                    icon={<WarningAmberIcon />}
                    label={`Mocked ×${user.mockedLocationReports}`}
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                  />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never signed in'}
                </Typography>
              </Box>
            </Box>
            {/* The card navigates; the switch must not take the reader with it. A phone has no
                hover, so the switch carries a visible label instead of a tooltip. */}
            <Box
              component="label"
              onClick={(e) => e.stopPropagation()}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, cursor: 'pointer' }}
            >
              <Switch
                checked={hasUserFlag(user.userFlags, UserFlags.CanCreateSilentAdventures)}
                onChange={() => setSilentTarget(user)}
                disabled={!isAdmin || togglingSilentFlag}
                color="primary"
                size="small"
                inputProps={{ 'aria-label': 'Can create silent adventures' }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                Silent
              </Typography>
            </Box>
          </Box>
        )}
        pagerPosition="both"
        getRowKey={(user) => user.id}
        loading={isLoading}
        error={error ? errorMessage(error, 'Failed to fetch users') : null}
        emptyMessage="No users found"
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onRowClick={(user) => navigate(`/users/${user.id}`)}
        sortBy={sortBy}
        sortDescending={sortDescending}
        onSortChange={handleSort}
      />

      <ConfirmDialog
        open={!!silentTarget}
        title={silentTargetHas ? 'Remove silent permission?' : 'Allow silent adventures?'}
        message={
          silentTarget
            ? silentTargetHas
              ? `${silentTarget.displayName} will no longer be able to create silent adventures.`
              : `${silentTarget.displayName} will be able to create silent adventures.`
            : undefined
        }
        confirmLabel={silentTargetHas ? 'Remove' : 'Allow'}
        destructive={silentTargetHas}
        loading={togglingSilentFlag}
        onConfirm={() => silentTarget && toggleSilentFlag(silentTarget)}
        onClose={() => setSilentTarget(null)}
      />
    </PageShell>
  )
}
